import { ASSET_METADATA_KEY } from '@/features/asset-metadata/config/constants';
import { ASSETS_KEY } from '@/features/assets/config/constants';
import { DELIVERY_QUERY_KEY } from '@/features/delivery/config/constants';
import {
  batchCreateFieldLogSessions,
  batchDeleteFieldLogSessions,
  batchPatchFieldLogSessions,
  batchPatchFieldLogVisits,
  changeFieldLogFootage,
  createFieldLogVisit,
  deleteEmptyFieldLogVisit,
  deleteFieldLogSession,
  endFieldLogSession,
  fetchFieldLogFootage,
  fetchFieldLogFootageCandidates,
  fetchFieldLogFootageLinks,
  fetchFieldLogFootageLookup,
  fetchFieldLogOrganizationOptions,
  fetchFieldLogSessions,
  fetchFieldLogVisit,
  fetchFieldLogVisits,
  patchFieldLogSession,
  patchFieldLogVisit,
  putFieldLogFootageReview,
  startFieldLogSession,
  unlinkFieldLogFootage,
} from '@/features/field-log/api/fieldLog';
import {
  FIELD_LOG_KEY,
  FIELD_LOG_ORGANIZATION_OPTIONS_PAGE_LIMIT,
  FIELD_LOG_PAGE_LIMIT,
} from '@/features/field-log/config/constants';
import {
  fieldLogAssetLinkBatches,
  indexFieldLogAssetLinks,
} from '@/features/field-log/lib/assetLinks';
import { fieldLogFootageLookupView } from '@/features/field-log/lib/footageLookup';
import type {
  CreateFieldLogSessionsBatchInput,
  CreateFieldLogVisitInput,
  DeleteFieldLogSessionsBatchInput,
  FieldLogFootageChangeIntent,
  FieldLogFootageLinkLookupItem,
  FieldLogFootageLookupRequest,
  FieldLogFootageReviewIntent,
  FieldLogOrganizationOperation,
  FieldLogResumeResult,
  FieldLogSessionsRequest,
  FieldLogVisitQuery,
  FieldLogVisitsFilters,
  FootageCandidatesQuery,
  PatchFieldLogSessionInput,
  PatchFieldLogSessionsBatchInput,
  PatchFieldLogVisitInput,
  PatchFieldLogVisitsBatchInput,
  ResumeFieldLogSessionInput,
  SessionId,
  StartFieldLogSessionInput,
  VisitId,
} from '@/features/field-log/types/fieldLog';
import { RAW_FOOTAGE_KEY } from '@/features/raw-footage/config/constants';
import { SUPERVISOR_KEY } from '@/features/supervisor/config/constants';
import { invalidateSupervisorFootageReviewQueries } from '@/features/supervisor/services/supervisorQueries';
import { ApiHttpError } from '@/shared/api/client';
import { useCommandKey } from '@/shared/hooks/useCommandKey';
import {
  infiniteQueryOptions,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Reads and writes over the two-entity contract.
 *
 * Visit detail is the recovery source: it carries the `cameras` grouping with
 * each session's `recording` flag, so every session mutation invalidates the owning
 * visit's detail rather than maintaining a separate session cache.
 */
const fieldLogKeys = {
  all: [FIELD_LOG_KEY] as const,
  lists: () => [FIELD_LOG_KEY, 'list'] as const,
  list: (filters: FieldLogVisitsFilters = {}, limit = FIELD_LOG_PAGE_LIMIT) =>
    [FIELD_LOG_KEY, 'list', filters, limit] as const,
  details: (visitId: VisitId) => [FIELD_LOG_KEY, 'visit-detail', visitId] as const,
  detail: (visitId: VisitId, query: FieldLogVisitQuery) =>
    [
      FIELD_LOG_KEY,
      'visit-detail',
      visitId,
      query.organizationId ?? 'all-organizations',
      query.projectId ?? 'all-projects',
      query.toleranceMinutes ?? 'default',
      query.include ?? [],
    ] as const,
  footage: (sessionId: SessionId) => [FIELD_LOG_KEY, 'session-footage', sessionId] as const,
  footageLinks: (assetIds: readonly string[]) =>
    [FIELD_LOG_KEY, 'footage-links', assetIds] as const,
  sessionLists: () => [FIELD_LOG_KEY, 'sessions'] as const,
  sessions: (request: FieldLogSessionsRequest) => [FIELD_LOG_KEY, 'sessions', request] as const,
  candidates: (sessionId: SessionId, query: FootageCandidatesQuery = {}) =>
    [
      FIELD_LOG_KEY,
      'session-candidates',
      sessionId,
      query.organizationId ?? 'all-organizations',
      query.projectId ?? 'all-projects',
      query.toleranceMinutes ?? 'default',
      query.include ?? [],
    ] as const,
  footageLookup: (request: FieldLogFootageLookupRequest | null) =>
    [FIELD_LOG_KEY, 'footage-lookup', request] as const,
  organizations: (operation: FieldLogOrganizationOperation) =>
    [FIELD_LOG_KEY, 'organization-options', operation] as const,
};

const FIELD_LOG_MATCH_INCLUDE = ['footageMatch'] as const;
export function fieldLogVisitsQuery(
  filters: FieldLogVisitsFilters = {},
  limit = FIELD_LOG_PAGE_LIMIT,
) {
  return infiniteQueryOptions({
    queryKey: fieldLogKeys.list(filters, limit),
    queryFn: ({ pageParam, signal }) =>
      fetchFieldLogVisits(
        {
          ...filters,
          cursor: pageParam,
          limit,
        },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    throwOnError: (_error, query) => query.state.data === undefined,
  });
}

export function fieldLogOrganizationOptionsQuery(
  operation: FieldLogOrganizationOperation,
  enabled = true,
) {
  return infiniteQueryOptions({
    queryKey: fieldLogKeys.organizations(operation),
    queryFn: ({ pageParam, signal }) =>
      fetchFieldLogOrganizationOptions(
        {
          operation,
          cursor: pageParam,
          limit: FIELD_LOG_ORGANIZATION_OPTIONS_PAGE_LIMIT,
        },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
    retry: false,
  });
}

export function fieldLogVisitQuery(visitId: VisitId) {
  return queryOptions({
    queryKey: fieldLogKeys.detail(visitId, {}),
    queryFn: ({ signal }) => fetchFieldLogVisit(visitId, {}, { signal }),
    retry: false,
  });
}

function fieldLogFootageQuery(sessionId: SessionId, enabled = true) {
  return queryOptions({
    queryKey: fieldLogKeys.footage(sessionId),
    queryFn: ({ signal }) => fetchFieldLogFootage(sessionId, { signal }),
    enabled: Boolean(sessionId) && enabled,
    retry: false,
  });
}

function fieldLogFootageLinksQuery(assetIds: readonly string[], enabled: boolean) {
  return queryOptions({
    queryKey: fieldLogKeys.footageLinks(assetIds),
    queryFn: ({ signal }) => fetchFieldLogFootageLinks(assetIds, { signal }),
    enabled: enabled && assetIds.length > 0,
    retry: false,
  });
}

export interface FieldLogSessionOptions
  extends Pick<FieldLogSessionsRequest, 'q' | 'recording' | 'organizationId'> {
  enabled?: boolean;
}

/** Remote, cursor-paginated target Sessions for asset-side association. */
export function fieldLogSessionsQuery({
  q,
  organizationId,
  recording = false,
  enabled = true,
}: FieldLogSessionOptions = {}) {
  const normalizedQuery = q?.trim() || undefined;
  const request = {
    q: normalizedQuery,
    recording,
    organizationId,
  } satisfies FieldLogSessionsRequest;
  return infiniteQueryOptions({
    queryKey: fieldLogKeys.sessions(request),
    queryFn: ({ pageParam, signal }) =>
      fetchFieldLogSessions(
        { ...request, cursor: pageParam, limit: FIELD_LOG_PAGE_LIMIT },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    retry: false,
    throwOnError: false,
  });
}

function fieldLogCandidatesQuery(
  sessionId: SessionId,
  enabled = true,
  query: FootageCandidatesQuery = {},
) {
  const request = { ...query, include: FIELD_LOG_MATCH_INCLUDE } satisfies FootageCandidatesQuery;
  return queryOptions({
    queryKey: fieldLogKeys.candidates(sessionId, request),
    queryFn: ({ signal }) => fetchFieldLogFootageCandidates(sessionId, request, { signal }),
    enabled: Boolean(sessionId) && enabled,
    retry: false,
    staleTime: 0,
  });
}

function fieldLogFootageLookupQuery(request: FieldLogFootageLookupRequest | null, enabled = true) {
  return queryOptions({
    queryKey: fieldLogKeys.footageLookup(request),
    queryFn: request ? ({ signal }) => fetchFieldLogFootageLookup(request, { signal }) : skipToken,
    enabled: request !== null && enabled,
    retry: false,
    select: fieldLogFootageLookupView,
  });
}

export function useFieldLogVisitsSuspense(
  filters: FieldLogVisitsFilters = {},
  limit = FIELD_LOG_PAGE_LIMIT,
) {
  return useSuspenseInfiniteQuery(fieldLogVisitsQuery(filters, limit));
}

export function useFieldLogOrganizationOptions(
  operation: FieldLogOrganizationOperation,
  enabled = true,
) {
  return useInfiniteQuery(fieldLogOrganizationOptionsQuery(operation, enabled));
}

export function useFieldLogVisit(visitId: VisitId, enabled = true) {
  return useQuery({ ...fieldLogVisitQuery(visitId), enabled: Boolean(visitId) && enabled });
}

export function useFieldLogVisitSuspense(visitId: VisitId) {
  return useSuspenseQuery(fieldLogVisitQuery(visitId));
}

export function useFieldLogFootage(sessionId: SessionId, enabled = true) {
  return useQuery(fieldLogFootageQuery(sessionId, enabled));
}

/**
 * Resolves current Session ownership for loaded assets in bounded API batches. Missing
 * rows intentionally remain absent from the map so callers never confuse a failed
 * or incomplete lookup with an unlinked asset.
 */
export function useFieldLogAssetLinks(assetIds: readonly string[], enabled = true) {
  const batches = useMemo(() => fieldLogAssetLinkBatches(assetIds), [assetIds]);
  const results = useQueries({
    queries: batches.map((batch) => fieldLogFootageLinksQuery(batch, enabled)),
  });
  const responses = results.flatMap((result) => (result.data ? [result.data] : []));
  const data = responses.length === batches.length ? indexFieldLogAssetLinks(responses) : undefined;
  const error = results.find((result) => result.isError)?.error ?? null;

  return {
    data: data as ReadonlyMap<string, FieldLogFootageLinkLookupItem> | undefined,
    error,
    isError: error !== null,
    isPending: results.some((result) => result.isPending),
  };
}

export function useFieldLogSessions(options: FieldLogSessionOptions = {}) {
  return useInfiniteQuery(fieldLogSessionsQuery(options));
}

export function useFieldLogCandidates(
  sessionId: SessionId,
  enabled = true,
  query: FootageCandidatesQuery = {},
) {
  return useQuery(fieldLogCandidatesQuery(sessionId, enabled, query));
}

/** Exact filename lookup for manually locating footage outside suggested candidates. */
export function useFieldLogFootageLookup(
  request: FieldLogFootageLookupRequest | null,
  enabled = true,
) {
  return useQuery(fieldLogFootageLookupQuery(request, enabled));
}

function useInvalidateSessions() {
  const queryClient = useQueryClient();
  return (visitId?: VisitId) => {
    void queryClient.invalidateQueries({ queryKey: fieldLogKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: fieldLogKeys.sessionLists() });
    void queryClient.invalidateQueries({ queryKey: [SUPERVISOR_KEY] });
    if (visitId) {
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.details(visitId) });
    }
  };
}

function invalidateFieldLogBatch(
  queryClient: ReturnType<typeof useQueryClient>,
  { visitId, affectsFootage }: { visitId?: string; affectsFootage?: boolean } = {},
) {
  void queryClient.invalidateQueries({ queryKey: fieldLogKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: fieldLogKeys.sessionLists() });
  void queryClient.invalidateQueries({ queryKey: [SUPERVISOR_KEY] });
  if (visitId)
    void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'visit-detail', visitId] });
  if (!affectsFootage) return;
  void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'visit-detail'] });
  void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'session-footage'] });
  void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'session-candidates'] });
  void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'footage-links'] });
  void queryClient.invalidateQueries({ queryKey: [RAW_FOOTAGE_KEY] });
}

export function useBatchCreateFieldLogSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      visitId,
      input,
    }: { visitId: string; input: CreateFieldLogSessionsBatchInput }) =>
      batchCreateFieldLogSessions(visitId, input),
    onSuccess: (_result, { visitId }) => invalidateFieldLogBatch(queryClient, { visitId }),
  });
}

export function useBatchPatchFieldLogSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input }: { visitId: string; input: PatchFieldLogSessionsBatchInput }) =>
      batchPatchFieldLogSessions(input),
    onSuccess: (_result, { visitId }) => invalidateFieldLogBatch(queryClient, { visitId }),
  });
}

export function useBatchPatchFieldLogVisits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input }: { visitId: string; input: PatchFieldLogVisitsBatchInput }) =>
      batchPatchFieldLogVisits(input),
    onSuccess: (_result, { visitId }) => invalidateFieldLogBatch(queryClient, { visitId }),
  });
}

export function useBatchDeleteFieldLogSessions(visitId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DeleteFieldLogSessionsBatchInput) => batchDeleteFieldLogSessions(input),
    onSuccess: () => invalidateFieldLogBatch(queryClient, { visitId, affectsFootage: true }),
  });
}

export function useCreateFieldLogVisit() {
  const invalidate = useInvalidateSessions();
  return useMutation({
    mutationFn: (input: CreateFieldLogVisitInput) => createFieldLogVisit(input),
    // The created visit is not seeded into the detail cache: create returns a
    // Visit without the `cameras` grouping the detail query is typed on.
    onSuccess: () => invalidate(),
  });
}

export function usePatchFieldLogVisit(visitId: VisitId) {
  const invalidate = useInvalidateSessions();
  return useMutation({
    mutationFn: (input: PatchFieldLogVisitInput) => patchFieldLogVisit(visitId, input),
    onSuccess: () => invalidate(visitId),
  });
}

/** Deletes an empty visit and refreshes every filtered Visit list. */
export function useDeleteEmptyFieldLogVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visitId: VisitId) => deleteEmptyFieldLogVisit(visitId),
    onSuccess: (_result, visitId) => {
      queryClient.removeQueries({ queryKey: fieldLogKeys.details(visitId) });
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: [SUPERVISOR_KEY] });
    },
  });
}

export function useStartFieldLogSession(visitId: VisitId) {
  const invalidate = useInvalidateSessions();
  return useMutation({
    mutationFn: (input: StartFieldLogSessionInput) => startFieldLogSession(visitId, input),
    onSuccess: () => invalidate(visitId),
  });
}

/**
 * Starts a new recording from a completed one, then copies the editable details
 * that a runner should not have to re-enter. A failed copy is deliberately
 * returned as a partial result: the recording is real and must remain visible
 * for manual completion rather than being deleted.
 */
export function useResumeFieldLogSession(visitId: VisitId) {
  const invalidate = useInvalidateSessions();
  return useMutation({
    mutationFn: async (input: ResumeFieldLogSessionInput): Promise<FieldLogResumeResult> => {
      const session = await startFieldLogSession(visitId, { opsCode: input.opsCode });
      try {
        await patchFieldLogSession(session.sessionId, {
          operatorId: input.operatorId,
          note: input.note,
        });
        return { kind: 'completed', session };
      } catch (error) {
        return { kind: 'details-not-copied', session, error };
      }
    },
    onSuccess: () => invalidate(visitId),
  });
}

export function usePatchFieldLogSession(visitId: VisitId) {
  const invalidate = useInvalidateSessions();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      input,
    }: { sessionId: SessionId; input: PatchFieldLogSessionInput }) =>
      patchFieldLogSession(sessionId, input),
    onSuccess: (_result, { sessionId }) => {
      invalidate(visitId);
      void queryClient.invalidateQueries({
        queryKey: [FIELD_LOG_KEY, 'session-candidates', sessionId],
      });
    },
  });
}

export function useEndFieldLogSession(visitId: VisitId) {
  const invalidate = useInvalidateSessions();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, endedAt }: { sessionId: SessionId; endedAt: string }) =>
      endFieldLogSession(sessionId, endedAt),
    onSuccess: (_result, { sessionId }) => {
      invalidate(visitId);
      void queryClient.invalidateQueries({
        queryKey: [FIELD_LOG_KEY, 'session-candidates', sessionId],
      });
    },
  });
}

/** Soft-delete one recording and refresh its visit plus linked-footage query scopes. */
export function useDeleteFieldLogSession(visitId: VisitId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: SessionId) => deleteFieldLogSession(sessionId),
    onSuccess: async (_result, sessionId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: fieldLogKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: fieldLogKeys.sessionLists() }),
        queryClient.invalidateQueries({ queryKey: fieldLogKeys.details(visitId) }),
        queryClient.invalidateQueries({ queryKey: fieldLogKeys.footage(sessionId) }),
        queryClient.invalidateQueries({
          queryKey: [FIELD_LOG_KEY, 'session-candidates', sessionId],
        }),
        queryClient.invalidateQueries({ queryKey: [SUPERVISOR_KEY] }),
      ]);
    },
  });
}

/** Writes one explicit review decision with optimistic review-version protection. */
export function usePutFieldLogFootageReview(sessionId: SessionId, visitId: VisitId) {
  const queryClient = useQueryClient();
  const commandKey = useCommandKey();
  return useMutation({
    mutationFn: (intent: FieldLogFootageReviewIntent) =>
      putFieldLogFootageReview(sessionId, {
        ...intent,
        idempotencyKey: commandKey.reserve(intent),
      }),
    onSuccess: (result) => {
      commandKey.reset();
      for (const affectedVisitId of result.affectedVisitIds) {
        void queryClient.invalidateQueries({ queryKey: fieldLogKeys.details(affectedVisitId) });
      }
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.sessionLists() });
      invalidateSupervisorFootageReviewQueries(queryClient);
    },
    onError: (error) => {
      if (!(error instanceof ApiHttpError) || (error.status !== 409 && error.status !== 404))
        return;
      if (error.status === 409) commandKey.reset();
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.sessionLists() });
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.details(visitId) });
      invalidateSupervisorFootageReviewQueries(queryClient);
    },
  });
}

/**
 * Atomically adds unlinked footage, moves it from another session onto this one, or removes it.
 * A command key is retained for an uncertain retry of the same logical change.
 */
export function useChangeFieldLogFootage(sessionId: SessionId | '') {
  const queryClient = useQueryClient();
  const commandKey = useCommandKey();
  return useMutation({
    mutationFn: (intent: FieldLogFootageChangeIntent) => {
      if (!sessionId) throw new Error('A Field Log Session ID is required.');
      return changeFieldLogFootage(sessionId, {
        ...intent,
        idempotencyKey: commandKey.reserve(intent),
      });
    },
    onSuccess: (result, intent) => {
      commandKey.reset();
      const affectedSessionIds = new Set(
        sessionId ? [sessionId, ...result.affectedSessionIds] : result.affectedSessionIds,
      );
      const assetIds = new Set([...intent.add, ...intent.remove].map((item) => item.assetId));
      for (const affectedSessionId of affectedSessionIds) {
        void queryClient.invalidateQueries({ queryKey: fieldLogKeys.footage(affectedSessionId) });
      }
      for (const affectedVisitId of result.affectedVisitIds) {
        void queryClient.invalidateQueries({ queryKey: fieldLogKeys.details(affectedVisitId) });
      }
      void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'session-candidates'] });
      void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'footage-lookup'] });
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.sessionLists() });
      void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'footage-links'] });
      for (const assetId of assetIds) {
        void queryClient.invalidateQueries({ queryKey: [ASSET_METADATA_KEY, 'asset', assetId] });
      }
      void queryClient.invalidateQueries({ queryKey: [ASSETS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [RAW_FOOTAGE_KEY] });
      void queryClient.invalidateQueries({ queryKey: [SUPERVISOR_KEY] });
      if (result.affectedDeliveryEntryIds.length > 0) {
        void queryClient.invalidateQueries({ queryKey: [DELIVERY_QUERY_KEY] });
      }
    },
    onError: (error) => {
      // A decoded conflict is definitive (and atomically changed nothing), so a later
      // user retry must reserve a fresh key even when the selected assets are unchanged.
      if (error instanceof ApiHttpError && error.status === 409) commandKey.reset();
      // A 409 means another writer changed ownership; refetch before the user retries.
      if (sessionId) {
        void queryClient.invalidateQueries({ queryKey: fieldLogKeys.footage(sessionId) });
        void queryClient.invalidateQueries({
          queryKey: [FIELD_LOG_KEY, 'session-candidates', sessionId],
        });
      }
      void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'footage-lookup'] });
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.sessionLists() });
      void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'footage-links'] });
    },
  });
}

export function useUnlinkFieldLogFootage(sessionId: SessionId | '', visitId: VisitId | '') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => {
      if (!sessionId) throw new Error('A Field Log Session ID is required.');
      return unlinkFieldLogFootage(sessionId, assetId);
    },
    onSuccess: (_result, assetId) => {
      if (sessionId) {
        void queryClient.invalidateQueries({ queryKey: fieldLogKeys.footage(sessionId) });
        void queryClient.invalidateQueries({
          queryKey: [FIELD_LOG_KEY, 'session-candidates', sessionId],
        });
      }
      if (visitId) void queryClient.invalidateQueries({ queryKey: fieldLogKeys.details(visitId) });
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: fieldLogKeys.sessionLists() });
      void queryClient.invalidateQueries({ queryKey: [FIELD_LOG_KEY, 'footage-links'] });
      void queryClient.invalidateQueries({ queryKey: [ASSET_METADATA_KEY, 'asset', assetId] });
      void queryClient.invalidateQueries({ queryKey: [RAW_FOOTAGE_KEY] });
      void queryClient.invalidateQueries({ queryKey: [SUPERVISOR_KEY] });
    },
  });
}
